import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMantineColorScheme } from '@mantine/core';
import './FullscreenModal.scss';

/*
  Reusable fullscreen modal (portal) with fade/scale transition.
  Props:
    open: boolean
    onClose: () => void
    title: string (optional)
    children: ReactNode
    showClose: boolean (default true)
    initialFocusRef?: ref to focus when opened
    maxWidth?: number (default 1600)
*/
const FullscreenModal = ({
  open,
  onClose,
  title,
  children,
  showClose = true,
  initialFocusRef,
  maxWidth = 1600,
}) => {
  const { colorScheme } = useMantineColorScheme();
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const mountedRef = useRef(false);
  const [active, setActive] = React.useState(false);

  // Mount animation
  useEffect(() => {
    if (open) {
      mountedRef.current = true;
      requestAnimationFrame(() => setActive(true));
    } else if (mountedRef.current) {
      setActive(false);
    }
  }, [open]);

  // Close after animation ends (220ms) when unmounting
  const [visible, setVisible] = React.useState(open);
  useEffect(() => {
    if (open) {
      setVisible(true);
    } else if (visible) {
      const t = setTimeout(() => setVisible(false), 240);
      return () => clearTimeout(t);
    }
  }, [open, visible]);

  // ESC key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Focus management
  useEffect(() => {
    if (!open) return;
    const toFocus = initialFocusRef?.current || modalRef.current?.querySelector('[data-autofocus]') || modalRef.current;
    if (toFocus && typeof toFocus.focus === 'function') {
      setTimeout(() => toFocus.focus(), 10);
    }
  }, [open, initialFocusRef]);

  if (!visible) return null;

  const schemeClass = colorScheme === 'dark' ? 'dark' : 'light';

  return createPortal(
    <div
      ref={overlayRef}
      className={`app-modal-overlay ${active ? 'open' : ''} ${schemeClass}`}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Dialog'}
      onMouseDown={(e) => { if (e.target === overlayRef.current) onClose?.(); }}
    >
      <div
        ref={modalRef}
        className={`app-modal ${active ? 'open' : ''} ${schemeClass}`}
        style={{ maxWidth: '100vw', height: '100vh' }}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {(title || showClose) && (
          <div className={`app-modal-bar ${schemeClass}`}>
            <span className={`app-modal-title ${schemeClass}`}>{title}</span>
            {showClose && (
              <button
                type="button"
                className={`app-modal-close ${schemeClass}`}
                aria-label="Close"
                onClick={() => onClose?.()}
              >✕</button>
            )}
          </div>
        )}
        <div className={`app-modal-body ${schemeClass}`}>{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default FullscreenModal;
